export function fmtHours(h: number | null | undefined): string {
  if (h == null || isNaN(h)) return "—";
  return `${h.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;
}

export function fmtPct(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  return `${v.toFixed(1)}%`;
}

export function fmtInt(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  return Math.round(v).toLocaleString();
}

export function fmtARR(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${String(Math.round(v / 1_000))}K`;
  return `$${v.toLocaleString()}`;
}

const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Formats an ISO "YYYY-MM-DD" date-range pair as e.g. "3 Jan – 14 Feb 2026".
export function formatDateRange(from: string, to: string): string {
  const [, fm, fd] = from.split("-");
  const [fy, tm, td] = to.split("-");
  const fromLabel = `${String(parseInt(fd ?? "1", 10))} ${SHORT_MONTHS[parseInt(fm ?? "1", 10) - 1] ?? ""}`;
  const toLabel = `${String(parseInt(td ?? "1", 10))} ${SHORT_MONTHS[parseInt(tm ?? "1", 10) - 1] ?? ""} ${fy ?? ""}`;
  return `${fromLabel} – ${toLabel}`;
}

import { BRAND } from "./colors";

export function extendedRateColor(rate: number | null | undefined): string {
  if (rate == null) return "#888";
  if (rate < 10) return BRAND.green;
  if (rate < 25) return BRAND.blue;
  if (rate < 40) return BRAND.orange;
  return BRAND.red;
}

/**
 * Colour band for completion rate — higher is better.
 *   ≥90 → green, 75–90 → blue, 50–75 → amber, <50 → red.
 * Returns null when rate is null so callers can opt out of colouring.
 */
export function completionRateColor(rate: number | null | undefined): string | null {
  if (rate == null) return null;
  if (rate >= 90) return BRAND.green;
  if (rate >= 75) return BRAND.blue;
  if (rate >= 50) return BRAND.orange;
  return BRAND.red;
}

/** Lowercase band label, useful for stable data attributes in tests. */
export function completionRateBand(
  rate: number | null | undefined,
): "green" | "blue" | "amber" | "red" | null {
  if (rate == null) return null;
  if (rate >= 90) return "green";
  if (rate >= 75) return "blue";
  if (rate >= 50) return "amber";
  return "red";
}

export const TIER_LABELS = {
  high: "High Priority",
  watch_list: "Watch List",
  on_track: "On Track",
  excellent: "Excellent",
} as const;

export type TierId = keyof typeof TIER_LABELS;

// Health score data is considered stale when its underlying period is older than this.
const STALE_THRESHOLD_DAYS = 60;

// Days elapsed since the end of a period month expressed as "YYYY-MM".
// Uses the first day of the following month as the reference so the full
// period month is included (e.g. "2026-04" → compare from May 1).
export function daysSinceLatestPeriod(latestPeriodAt: string): number {
  const [yearStr, monthStr] = latestPeriodAt.split("-");
  const year = parseInt(yearStr ?? "0", 10);
  const month = parseInt(monthStr ?? "0", 10);
  // JS months are 0-indexed; (year, month, 1) = first day of the month after the period
  const periodEnd = new Date(year, month, 1);
  const now = new Date();
  return Math.floor((now.getTime() - periodEnd.getTime()) / 86_400_000);
}

export function isStale(latestPeriodAt: string | null | undefined): boolean {
  if (!latestPeriodAt) return false;
  return daysSinceLatestPeriod(latestPeriodAt) > STALE_THRESHOLD_DAYS;
}
